import { Card, Grid } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import dayjs from "dayjs";
import { useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import { getLocationStockMovement } from "services/data.inventory.service";
import { getSKUDetails } from "services/data.inventory.service";
import { LoadingDialog } from "ui-components/Alert";
import { CustomDateRangePicker } from "ui-components/CustomInput";
import { FormTitle } from "ui-components/Form";
import { ReadonlyText } from "ui-components/Form";
import { ListLayout } from "ui-components/Layout";
import BasicTable from "ui-components/tables/BasicTable";
import BasicTabs from "ui-components/tabs/BasicTabs";
import { locationStockMovementcolumns, skuDetailscolumns } from "./inventory-mgmt/columns";
import { Error_ } from "ui-components/Alert";
import { CSVLink } from "react-csv";
import { useTeamValue } from "providers/TeamContext";
import { ExportDialog } from "ui-components/Alert";
import useCsvDownloadHook from "hooks/useCsvDownloadHook";

const tabs = ["SKU Details", "Stock Movement"];

function InventorySKUDetails() {
  const { permissions, allowAll } = useTeamValue();
  const [status, setStatus] = useState("SKU Details");
  const location = useLocation();
  const { customerCode, warehouseCode, zoneID, locationID } = location?.state ?? {};

  const [alert, setAlert] = useState(null);
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const taskProps = { customerCode, warehouseCode, zoneID, locationID, setAlert, onError };

  const exportPermission = allowAll || (Boolean(permissions["Inventory Location List"]) && permissions["Inventory Location List"].findIndex(b => b === "export") !== -1);

  const render = () => {
    let task;
    switch (status) {
      case "SKU Details":
        task = <SKUDetails {...taskProps} />;
        break;
      case "Stock Movement":
        task = <StockMovement {...taskProps} exportPermission={exportPermission} />;
        break;
      case "Outbound Details":
        task = <OutboundDetails />;
        break;
    }
    return task;
  };
  return (
    <ListLayout>
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <Card sx={{ mb: 1, padding: "1rem" }}>
        <FormTitle title="Location Information" />
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <ReadonlyText
              field={{ name: "warehouseCode", label: "Warehouse Code", value: warehouseCode }}
            />
          </Grid>
          <Grid item xs={6}>
            <ReadonlyText field={{ name: "zoneID", label: "Zone ID", value: zoneID }} />
          </Grid>
          <Grid item xs={2}>
            <ReadonlyText field={{ name: "locationID", label: "Location ID", value: locationID }} />
          </Grid>
        </Grid>
        {render()}
      </Card>
    </ListLayout>
  );
}

export default InventorySKUDetails;

function SKUDetails({ customerCode, warehouseCode, zoneID, locationID, onError }) {
  const [list, setList] = useState([]);
  const { isFetching } = useQuery({
    queryKey: ["inventory-sku-details"],
    queryFn: () => getSKUDetails({ customerCode, warehouseCode, zoneID, locationID }),
    onSuccess: (data) => setList(data.data.data),
    onError,
  });

  const [columnFilters, setColumnFilters] = useState([]);
  const tableProps = {
    data: list,
    columns: skuDetailscolumns,
    columnFilters,
    setColumnFilters,
  };
  return (
    <>
      {isFetching && <LoadingDialog />}
      <BasicTable sx={{ mt: "2rem" }} {...tableProps} />;
    </>
  );
}
const date = dayjs(new Date()).format("YYYY-MM-DD");
const defaultStartDate = dayjs(new Date()).startOf("month").format("YYYY-MM-DD");

function StockMovement({ customerCode, warehouseCode, zoneID, locationID, setAlert, onError, exportPermission = false }) {
  const [list, setList] = useState([]);

  const [startDate, setStartDate] = useState(defaultStartDate);

  const [endDate, setEndDate] = useState(date);
  const { isDownloading, downloadCsv } = useCsvDownloadHook();
  const { isFetching, refetch } = useQuery({
    queryKey: ["inventory-stock-movement"],
    queryFn: () =>
      getLocationStockMovement({
        customerCode,
        warehouseCode,
        zoneID,
        locationID,
        startDate,
        endDate,
      }),
    onSuccess: (data) => {
      setAlert(null);
      setList(data.data.data);
    },
    retry: 0,
    onError,
  });
  const [filteredData, setFilteredData] = useState([]);
  const handleChange = useCallback((data) => {
    setFilteredData(data);
  }, []);

  const tableProps = {
    data: list,
    columns: locationStockMovementcolumns,
    onFilterRowsChange: handleChange,
  };
  const customProps = { startDate, setStartDate, endDate, setEndDate };
  const customComponent = (
    <MDBox margin="2rem 10px 0 0">
      <Grid justifyContent="flex-end" container spacing={3}>
        <Grid item sx={8}>
          <CustomDateRangePicker {...customProps} />
        </Grid>
        <Grid item sx={2}>
          <MDButton color="info" onClick={refetch}>
            search
          </MDButton>
        </Grid>
        <Grid item sx={2}>
          {/* <MDButton color="info" disabled={!exportPermission}>
            <CSVLink
              style={{ color: "white" }}
              filename={`${locationID}_${startDate}_${endDate}`}
              data={filteredData}
            >
              export
            </CSVLink>
          </MDButton> */}
          <MDButton
            color="info" disabled={!exportPermission}
            onClick={() => {
              const filename = `${customerCode}_${dayjs(new Date()).format("YYYYMMDD-HHmm")}_Location_${locationID}_${startDate}_${endDate}`;
              downloadCsv(filename, filteredData)
            }}
          >
            export
          </MDButton>
        </Grid>
      </Grid>
    </MDBox>
  );
  return (
    <>
      {isFetching && <LoadingDialog />}
      {isDownloading && <ExportDialog />}
      {customComponent}
      <BasicTable {...tableProps} />
    </>
  );
}
function OutboundDetails() {
  return <>outbound details</>;
}
